<script setup lang="ts">
import { ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import { useDefaultToast } from "@/composables/useDefaultToast";
import { useDeactivateWorkers } from "@/composables/workerActions";
import { useAuthStore } from "@/modules/auth/store/authStore";
import workerService from "@/modules/worker/services/workerService";
import { useConfigurationStore } from "@/store/configurationStore";

import type { NonDelegableFlowResult } from "@/components/modal/Profile/nonDelegable/types";
import type { WorkerDelegabilityOptimisticPayload } from "@/modules/worker/helpers/workerDelegabilityOptimistic";

import CancelUpcomingMissionsModal from "@/components/modal/Profile/nonDelegable/CancelUpcomingMissionsModal.vue";
import NonDelegableConfirmationModal from "@/components/modal/Profile/nonDelegable/NonDelegableConfirmationModal.vue";

interface NonDelegableFlowModalsProps {
    open: boolean;
    workerIds: string[];
    workerName?: string;
    reasonId: string;
    comment: string;
    onOptimisticUpdate?: (payload: WorkerDelegabilityOptimisticPayload) => void;
    onOptimisticRollback?: () => void;
}

const props = defineProps<NonDelegableFlowModalsProps>();

const emit = defineEmits<{
    close: [];
    complete: [result: NonDelegableFlowResult];
}>();

const { t } = useI18n();
const authStore = useAuthStore();
const configurationStore = useConfigurationStore();
const { toastError } = useDefaultToast();
const { deactivateWorkers, loading: isDeactivating } = useDeactivateWorkers();

const resolveReasonLabel = (reasonId: string) =>
    configurationStore.configuration.inactivatedReasons.find((reason) => reason.id === reasonId)?.reason ?? "";

const isConfirmationOpen = ref(false);
const isCancelMissionsOpen = ref(false);
const isRemovingFromAgency = ref(false);

const isMultipleWorkers = () => props.workerIds.length > 1;

const resetFlow = () => {
    isConfirmationOpen.value = false;
    isCancelMissionsOpen.value = false;
    isRemovingFromAgency.value = false;
};

const handleClose = () => {
    resetFlow();
    emit("close");
};

const handleRemoveFromAgency = async () => {
    const agencyId = authStore.currentAgency?.id;
    if (!agencyId) return;

    const optimisticPayload: WorkerDelegabilityOptimisticPayload = {
        action: "remove-from-agency",
        workerIds: props.workerIds,
        agencyId,
    };

    props.onOptimisticUpdate?.(optimisticPayload);
    isRemovingFromAgency.value = true;
    try {
        const result = await workerService.bulkRemoveFromAgency(agencyId, props.workerIds);
        const failedIds = result.failedIds ?? [];

        if (result.failedCount > 0) {
            props.onOptimisticRollback?.();
            const successIds = props.workerIds.filter((workerId) => !failedIds.includes(workerId));
            if (successIds.length === 0) {
                toastError(t("page.profile.removeFromAgency.error"));
                handleClose();
                return;
            }
            props.onOptimisticUpdate?.({
                action: "remove-from-agency",
                workerIds: successIds,
                agencyId,
            });
            emit("complete", { action: "remove-from-agency", workerIds: successIds });
            handleClose();
            return;
        }

        emit("complete", { action: "remove-from-agency", workerIds: props.workerIds });
        handleClose();
    } catch {
        props.onOptimisticRollback?.();
        toastError(t("page.profile.removeFromAgency.error"));
        handleClose();
    } finally {
        isRemovingFromAgency.value = false;
    }
};

const handleConfirmNonDelegable = () => {
    isConfirmationOpen.value = false;
    isCancelMissionsOpen.value = true;
};

const submitDeactivation = async (removeMission: boolean) => {
    const optimisticPayload: WorkerDelegabilityOptimisticPayload = {
        action: "deactivate",
        workerIds: props.workerIds,
        reasonId: props.reasonId,
        comment: props.comment,
        reasonLabel: resolveReasonLabel(props.reasonId),
        missionsCancelled: removeMission,
    };

    props.onOptimisticUpdate?.(optimisticPayload);
    try {
        await deactivateWorkers({
            workerIds: props.workerIds,
            data: {
                reasonId: props.reasonId,
                removeMission,
                comment: props.comment || undefined,
            },
        });
        emit("complete", {
            action: "deactivate",
            workerIds: props.workerIds,
            reasonId: props.reasonId,
            comment: props.comment,
            missionsCancelled: removeMission,
        });
        handleClose();
    } catch {
        props.onOptimisticRollback?.();
        toastError(t("page.worker.profile.deactivate.error"));
        handleClose();
    }
};

const handleSkipCancelMissions = () => submitDeactivation(false);
const handleCancelMissions = () => submitDeactivation(true);

watch(
    () => props.open,
    (isOpen) => {
        if (isOpen) {
            isConfirmationOpen.value = true;
            isCancelMissionsOpen.value = false;
        } else {
            resetFlow();
        }
    }
);
</script>

<template>
    <NonDelegableConfirmationModal
        :open="isConfirmationOpen"
        :is-loading-remove-from-agency="isRemovingFromAgency"
        @close="handleClose"
        @remove-from-agency="handleRemoveFromAgency"
        @confirm="handleConfirmNonDelegable"
    />
    <CancelUpcomingMissionsModal
        :open="isCancelMissionsOpen"
        :worker-name="workerName"
        :is-multiple-workers="isMultipleWorkers()"
        :is-loading="isDeactivating"
        @close="handleClose"
        @skip-cancel="handleSkipCancelMissions"
        @cancel-missions="handleCancelMissions"
    />
</template>
